import React, { useState, useEffect, useMemo } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { TableBasicClick } from 'components';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { guid } from 'helpers/formatter';
import { useSavedLocations, useAddLocation, useDeleteLocation, useEditLocation } from 'hooks/locations.hook';
import { date, convertIntToDecimalCoord } from 'helpers/formatter';

export { ManageSavedLocations };

function ManageSavedLocations() {
  const [validated, setValidated] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const { data: locationsData, error: locationsError, isError: locationsIsError, isLoading: locationsIsLoading } = useSavedLocations();
  useEffect(() => {
    if (locationsData !== undefined) {
      setLocations(locationsData);
    }
  }, [locationsData]);

  const showAddDialogClick = () => {
    setShowAddDialog(true);
  }

  const handleDeleteClose = () => setShowDeleteDialog(false);
  const handleEditLocationClose = () => setShowEditDialog(false);

  const editLocationMutation = useEditLocation();
  const deleteLocationMutation = useDeleteLocation();
  const addLocationMutation = useAddLocation();

  const handleDelete = () => {
    deleteLocationMutation.mutate({
      id: selectedLocation.id,
    });
    setShowDeleteDialog(false);
    setShowEditDialog(false);
  };

  const handleAddSubmit = (event) => {
    const form = event.currentTarget;

    if (form.checkValidity() === true) {
      const id = guid();
      let location = {
        id: id,
        name: form.formName.value,
        isSaved: true
      };

      //addLocationMutation.mutate(location);
      setShowAddDialog(false);
      setValidated(false);
    }
    else {
      setValidated(true);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handleEditSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === true) {
      let location = {
        id: selectedLocation.id,
        name: form.formName.value,
        isSaved: isSaved,
      };

      editLocationMutation.mutate(location);
      setShowEditDialog(false);
      setIsSaved(false);
      setValidated(false);
    }
    else {
      setValidated(true);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const formatTrProps = (state = {}) => {
    return {
      onClick: () => {
        setSelectedLocation(state.original);
        setIsSaved(state.original.isSaved);
        setShowEditDialog(true);
      }
    };
  };
  const columns = useMemo(
    () => [
      {
        Header: "Map",
        accessor: "id",
        Cell: ({ row }) => (
          <>
            {row.original.latitude !== 0 && row.original.longitude !== 0 ? (
              <div className="location-img-default"><iframe title='map' className='saved-locations-map' src={'https://maps.google.com/maps?q=' + convertIntToDecimalCoord(row.original.latitude) + ',' + convertIntToDecimalCoord(row.original.longitude) + '&t=&z=16&ie=UTF8&iwloc=&output=embed'}></iframe></div>
            ) : (
              <div>No coordinates</div>
            )}
          </>
        ),
        width: "200px"
      },
      {
        Header: "Name",
        accessor: "name"
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: ({ row }) => (
          <>
            {row.original.address === null ? (
              <></>
            ) : (
              row.original.address
            )}
          </>
        )
      },
      {
        Header: "Date",
        accessor: "durationStart",
        Cell: ({ row }) => (
          date(row.original.durationStart)
        )
      },
      {
        Header: "Modified",
        accessor: "modified",
        Cell: ({ row }) => (
          <>
            {row.original.modified === null ? (
              date(row.original.created)
            ) : (
              date(row.original.modified)
            )}
          </>
        )
      }
    ],
    []
  );

  return (
    <div>
      <p>&nbsp;</p>
      {locations && <TableBasicClick responsive columns={columns} data={locations} formatRowProps={(state) => formatTrProps(state)} />}
      {locationsIsLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div>}
      {locationsIsError && <div className="text-danger">Error loading locations: {locationsError.message}</div>}

      <Modal
        size="lg"
        show={showAddDialog}
        onHide={() => { setShowAddDialog(false); }}
        aria-labelledby="modal-add-location"
      >
        <Form noValidate validated={validated} onSubmit={handleAddSubmit}>
          <Modal.Header closeButton closeVariant='white'>
            <Modal.Title id="modal-add-location">Add to Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control required maxLength="50" type="text" placeholder="Enter a name" />
              <Form.Control.Feedback type="invalid">Please enter a name.</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="submit">Save</Button>&nbsp;
            <Button variant="secondary" type="button" onClick={() => { setShowAddDialog(false); }}>Cancel</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        size="lg"
        show={showEditDialog}
        onHide={() => { setShowEditDialog(false); }}
        aria-labelledby="modal-edit-location"
      >
        <Form noValidate validated={validated} onSubmit={handleEditSubmit}>
          <Modal.Header closeButton closeVariant='white'>
            <Modal.Title id="modal-edit-location">{selectedLocation.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control required maxLength="50" type="text" defaultValue={selectedLocation.name} placeholder="Enter a name" />
              <Form.Control.Feedback type="invalid">Please enter a name.</Form.Control.Feedback>
            </Form.Group>
            {selectedLocation.durationEnd !== null && 
                <Form.Group className="mb-3" controlId="formIsSaved">
                  <Form.Label>Saved</Form.Label>
                  <Form.Check type="switch" defaultChecked={selectedLocation.isSaved} onChange={(e) => { setIsSaved(!isSaved); }} />
                  <br />
                </Form.Group>
            }
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="submit">Save</Button>&nbsp;
            <Button variant="secondary" type="button" onClick={handleEditLocationClose}>Cancel</Button>
            {selectedLocation.durationEnd === null && 
              <Button variant="danger" className='modal-btn-right' onClick={() => setShowDeleteDialog(true)}>Delete</Button>
            }
            <p className="last-modified">Last Modified: {selectedLocation.modified === undefined ? <>{date(selectedLocation.created)}</> : <>{date(selectedLocation.modified)}</>}</p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        aria-labelledby="modal-delete"
      >
        <Modal.Header closeButton closeVariant='white'>
          <Modal.Title id="modal-delete">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you would like to delete:</p>
          {showDeleteDialog && <div><p>{selectedLocation.name}</p></div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" type="submit" onClick={(handleDelete)}>Yes</Button>&nbsp;
          <Button variant="secondary" type="button" onClick={handleDeleteClose}>No</Button>
          <p className="last-modified">Last Modified: {selectedLocation.modified === undefined ? <>{date(selectedLocation.created)}</> : <>{date(selectedLocation.modified)}</>}</p>
        </Modal.Footer>
      </Modal>
    </div>
  );
}